import {
  TASK_AUTOMATION_STATE_LABELS,
  TASK_DESCRIPTION_MAX,
  TASK_STATUS_LABELS,
  TASK_STATUSES,
  MANUAL_TASK_TYPES,
  TASK_TYPE_LABELS,
  type ManualTaskType,
  type TaskAutomationState,
  type TaskStatus,
} from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type TaskPatchBody, type TaskView } from '../../lib/client.ts';
import { useSession } from '../../lib/session.tsx';
import { Badge } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';
import { Input, Textarea } from '../ui/input.tsx';
import { Markdown } from '../ui/markdown.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { TaskRecordLink } from './task-record-link.tsx';

/** Radix Select has no empty value, so "no type" is spelled `none`. */
export const NO_TYPE = 'none';

export const STATUS_BADGE: Record<TaskStatus, 'secondary' | 'brand' | 'success'> = {
  todo: 'secondary',
  in_progress: 'brand',
  done: 'success',
};

export const AUTOMATION_BADGE: Record<
  TaskAutomationState,
  'secondary' | 'warning' | 'success' | 'outline' | 'danger'
> = {
  scheduled: 'secondary',
  retrying: 'warning',
  sent: 'success',
  skipped: 'outline',
  failed: 'danger',
};

/** An automated task's delivery state, with the error or skip reason on hover. */
export function AutomationBadge({ task }: { task: TaskView }) {
  if (!task.automation) return null;
  const { state, error } = task.automation;
  return (
    <Badge
      variant={AUTOMATION_BADGE[state]}
      title={error ?? undefined}
      data-testid="task-automation"
    >
      {TASK_AUTOMATION_STATE_LABELS[state]}
    </Badge>
  );
}

/** Its run will still send: closing the task cancels that send. */
export const runPending = (t: TaskView) =>
  t.automation?.state === 'scheduled' || t.automation?.state === 'retrying';

/** A done automated task whose run is over stays done: the API refuses to reopen it. */
export const statusLocked = (t: TaskView) => t.automated && t.status === 'done' && !runPending(t);

/** `YYYY-MM-DD` in local time, what a date input holds. */
const toDateInput = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
/** A picked due date means 17:00 local that day, the same as the Add task form. */
const fromDateInput = (day: string) => (day ? new Date(`${day}T17:00:00`).toISOString() : null);

/**
 * A task's detail (TS-1740): title, status, due date, assignee and the Markdown description,
 * opened from a task's title in the record sidebar and My Tasks — and meant to be opened from
 * the task boards (TS-1742/1744) and Team Tasks (TS-1743) too, so it fetches nothing about
 * the task itself and refreshes every `['tasks', …]` query when it writes.
 *
 * Save sends ONE patch with only what changed, so a save is one audit row. `canEdit` is the
 * API's rule (the assignee, or anyone who can write to the record); `canDelete` too (the
 * creator, or anyone who can write to the record). Closing with unsaved edits asks first.
 */
export function TaskDetailDialog({
  task,
  canEdit,
  canDelete,
  onClose,
}: {
  task: TaskView;
  canEdit: boolean;
  canDelete: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const session = useSession();
  const members = useQuery({
    queryKey: ['users', session.me.organisation.id],
    queryFn: () => api.users(),
    staleTime: 60_000,
    enabled: canEdit,
  });
  const initialDue = toDateInput(task.dueAt);
  const [title, setTitle] = useState(task.title);
  const [status, setStatus] = useState<TaskStatus>(task.status);
  // Holds an automated task's type too, but only a manual task's type is ever sent (below).
  const [type, setType] = useState(task.type as ManualTaskType | null);
  const [due, setDue] = useState(initialDue);
  const [assignee, setAssignee] = useState(task.assignee?.id ?? '');
  const [description, setDescription] = useState(task.description ?? '');
  const [editingDescription, setEditingDescription] = useState(false);
  const [confirming, setConfirming] = useState<'delete' | 'discard' | null>(null);

  const pending = runPending(task);
  const cannotReopen = statusLocked(task);
  const patch: TaskPatchBody = {};
  if (title.trim() !== task.title) patch.title = title.trim();
  if (status !== task.status) patch.status = status;
  if (!task.automated && type !== task.type) patch.type = type;
  if (due !== initialDue) patch.dueAt = fromDateInput(due);
  if (assignee && assignee !== (task.assignee?.id ?? '')) patch.assigneeId = assignee;
  if (description.trim() !== (task.description ?? ''))
    patch.description = description.trim() || null;
  const dirty = Object.keys(patch).length > 0;

  const refresh = () => qc.invalidateQueries({ queryKey: ['tasks'] });
  const save = useMutation({
    mutationFn: () => api.tasks.patch(task.id, patch),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.tasks.remove(task.id),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  const error = save.error ?? remove.error;

  const requestClose = () => {
    if (canEdit && dirty) setConfirming('discard');
    else onClose();
  };
  // The current assignee stays in the list even when they are no longer an active member.
  const people: Array<{ id: string; name: string }> = (members.data ?? []).filter(
    (m) => m.status === 'active' || m.id === task.assignee?.id,
  );
  if (task.assignee && !people.some((m) => m.id === task.assignee?.id))
    people.unshift(task.assignee);

  return (
    <Dialog open onOpenChange={(open) => !open && requestClose()}>
      <DialogContent
        className="max-w-xl"
        data-testid="task-detail"
        // Not into the Title field: it opens selected, and a stray keystroke would replace it.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{task.title}</DialogTitle>
          <DialogDescription>
            On{' '}
            <TaskRecordLink
              task={task}
              // Leaving for the record closes the dialog; with unsaved edits, ask first instead.
              onClick={(e) => {
                if (canEdit && dirty) {
                  e.preventDefault();
                  setConfirming('discard');
                } else onClose();
              }}
            />
          </DialogDescription>
        </DialogHeader>

        {canEdit ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 text-sm font-medium sm:col-span-2">
              Title
              <Input
                aria-label="Title"
                value={title}
                maxLength={300}
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1.5 text-sm font-medium">
              Status
              <Select
                value={status}
                onValueChange={(v) => setStatus(v as TaskStatus)}
                disabled={cannotReopen}
              >
                <SelectTrigger aria-label="Status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {TASK_STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {task.automated && (cannotReopen || pending) && (
                <span
                  className="text-muted-foreground text-xs font-normal"
                  data-testid="task-status-note"
                >
                  {cannotReopen
                    ? 'Its run has finished, so it cannot reopen.'
                    : 'Marking it done cancels the send.'}
                </span>
              )}
            </label>
            {/* An automated task keeps its type (the API refuses a change): shown, not a field. */}
            {task.automated ? (
              <div className="flex flex-col gap-1.5 text-sm font-medium">
                Type
                <div
                  className="flex h-9 items-center gap-2 font-normal"
                  data-testid="task-type-fixed"
                >
                  {task.type && TASK_TYPE_LABELS[task.type]}
                  <Badge variant="brand">Automated</Badge>
                </div>
              </div>
            ) : (
              <label className="flex flex-col gap-1.5 text-sm font-medium">
                Type
                <Select
                  value={type ?? NO_TYPE}
                  onValueChange={(v) => setType(v === NO_TYPE ? null : (v as ManualTaskType))}
                >
                  <SelectTrigger aria-label="Type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_TYPE}>No type</SelectItem>
                    {MANUAL_TASK_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {TASK_TYPE_LABELS[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            )}
            <label className="flex flex-col gap-1.5 text-sm font-medium">
              Due date
              <Input
                aria-label="Due date"
                type="date"
                value={due}
                onChange={(e) => setDue(e.target.value)}
                // An automated task is due when its run sends; the API refuses moving it.
                disabled={task.automated}
              />
            </label>
            <label className="flex flex-col gap-1.5 text-sm font-medium">
              Assignee
              <Select value={assignee} onValueChange={setAssignee}>
                <SelectTrigger aria-label="Assignee">
                  <SelectValue placeholder="Former member" />
                </SelectTrigger>
                <SelectContent>
                  {people.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.id === session.me.user.id ? `${m.name} (me)` : m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          </div>
        ) : (
          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-muted-foreground text-xs">Status</dt>
              <dd>
                <Badge variant={STATUS_BADGE[task.status]}>{TASK_STATUS_LABELS[task.status]}</Badge>
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">Type</dt>
              <dd>
                {task.type ? TASK_TYPE_LABELS[task.type] : 'None'}
                {task.automated && (
                  <Badge variant="brand" className="ml-2">
                    Automated
                  </Badge>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">Due date</dt>
              <dd>{task.dueAt ? new Date(task.dueAt).toLocaleDateString() : 'None'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">Assignee</dt>
              <dd>{task.assignee?.name ?? 'Former member'}</dd>
            </div>
          </dl>
        )}

        <section className="flex flex-col gap-1.5" data-testid="task-description">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Description</h4>
            {canEdit && !editingDescription && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingDescription(true)}
                data-testid="edit-description"
              >
                <Pencil /> Edit
              </Button>
            )}
          </div>
          {editingDescription ? (
            <>
              <Textarea
                autoFocus
                aria-label="Description"
                value={description}
                maxLength={TASK_DESCRIPTION_MAX}
                onChange={(e) => setDescription(e.target.value)}
                className="min-h-32"
              />
              <p className="text-muted-foreground text-xs">
                Markdown supported. {description.length.toLocaleString()} /{' '}
                {TASK_DESCRIPTION_MAX.toLocaleString()}
              </p>
            </>
          ) : task.description ? (
            <Markdown>{task.description}</Markdown>
          ) : (
            <p className="text-muted-foreground text-sm">No description.</p>
          )}
        </section>

        {task.automated && <AutomationSection task={task} />}

        <p className="text-muted-foreground text-xs" data-testid="task-provenance">
          Created by{' '}
          {task.createdBy?.name ??
            (task.createdByAutomation
              ? task.createdByAutomation.name
                ? `the automation "${task.createdByAutomation.name}"`
                : 'a deleted automation'
              : 'a former member')}{' '}
          on {new Date(task.createdAt).toLocaleDateString()}
          {task.completedAt && ` · Completed ${new Date(task.completedAt).toLocaleDateString()}`}
        </p>

        {error && (
          <p className="text-danger text-sm" role="alert">
            {describeError(error)}
          </p>
        )}

        {confirming === 'delete' ? (
          <DialogFooter className="items-center" data-testid="confirm-delete">
            <span className="mr-auto text-sm">Delete this task?</span>
            <Button variant="ghost" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              data-testid="confirm-delete-task"
            >
              Delete
            </Button>
          </DialogFooter>
        ) : confirming === 'discard' ? (
          <DialogFooter className="items-center" data-testid="confirm-discard">
            <span className="mr-auto text-sm">Discard your changes?</span>
            <Button variant="ghost" onClick={() => setConfirming(null)}>
              Keep editing
            </Button>
            <Button variant="destructive" onClick={onClose} data-testid="discard-changes">
              Discard
            </Button>
          </DialogFooter>
        ) : (
          <DialogFooter>
            {canDelete && (
              <Button
                variant="ghost"
                className="mr-auto"
                onClick={() => setConfirming('delete')}
                data-testid="delete-task"
              >
                <Trash2 /> Delete
              </Button>
            )}
            {canEdit ? (
              <>
                <Button variant="ghost" onClick={requestClose}>
                  Cancel
                </Button>
                <Button
                  onClick={() => save.mutate()}
                  disabled={!dirty || !title.trim() || save.isPending}
                  data-testid="save-task"
                >
                  Save
                </Button>
              </>
            ) : (
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Body text as sent, indented when it is JSON. */
function pretty(body: string) {
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
    // eslint-disable-next-line crm/no-silent-catch -- probing JSON.parse; not JSON is shown as sent
  } catch {
    return body;
  }
}

/**
 * Where the run stands (sent, failed, skipped and why), and under Advanced what it sent and
 * what came back, read only when opened: the body is the whole project payload.
 */
function AutomationSection({ task }: { task: TaskView }) {
  const [open, setOpen] = useState(false);
  const run = useQuery({
    queryKey: ['tasks', 'automation', task.id],
    queryFn: () => api.tasks.automation(task.id),
    enabled: open,
  });
  const a = task.automation;
  return (
    <section className="flex flex-col gap-1.5 text-sm" data-testid="task-delivery">
      <h4 className="font-medium">Delivery</h4>
      {a ? (
        <p className="flex flex-wrap items-center gap-2">
          <AutomationBadge task={task} />
          {a.responseStatus !== null && <span>HTTP {a.responseStatus}</span>}
          {a.attempts > 1 && <span>{a.attempts} attempts</span>}
          {a.processedAt && <span>on {new Date(a.processedAt).toLocaleString()}</span>}
        </p>
      ) : (
        <p className="text-muted-foreground">No run found for this task.</p>
      )}
      {a?.error && <p className="text-danger">{a.error}</p>}
      {a && (
        <details
          className="border-border rounded-card border px-3 py-2"
          onToggle={(e) => setOpen(e.currentTarget.open)}
        >
          <summary className="cursor-pointer font-medium">Advanced</summary>
          {run.isPending && open && <p className="text-muted-foreground mt-2">Loading…</p>}
          {run.error && (
            <p className="text-danger mt-2" role="alert">
              {describeError(run.error)}
            </p>
          )}
          {run.data &&
            (run.data.request ? (
              <div className="mt-2 flex flex-col gap-2" data-testid="task-request">
                <p className="font-mono text-xs break-all">
                  {run.data.request.method} {run.data.request.url}
                </p>
                {run.data.request.body ? (
                  <pre className="bg-muted max-h-72 overflow-auto rounded p-2 text-xs">
                    {pretty(run.data.request.body)}
                  </pre>
                ) : (
                  <p className="text-muted-foreground text-xs">
                    Only people who can read automations and manage custom fields see what was sent.
                  </p>
                )}
                {run.data.responseExcerpt && (
                  <>
                    <p className="text-muted-foreground text-xs">
                      Response
                      {run.data.responseStatus !== null && ` (HTTP ${run.data.responseStatus})`}
                    </p>
                    <pre className="bg-muted max-h-40 overflow-auto rounded p-2 text-xs">
                      {run.data.responseExcerpt}
                    </pre>
                  </>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground mt-2">Nothing was sent yet.</p>
            ))}
        </details>
      )}
    </section>
  );
}
